import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, TrendingDown, Activity, Folder, CheckCircle2, Clock, AlertTriangle, Loader2, ArrowRight, Plus, Home, ChevronLeft, Sparkles, BookOpen, Building2, Trash2, LogOut, User, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GlobalDashboard = ({ onSelectProject, onBackToHome, onViewDocumentation }) => {
  const { user, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // All projects across customers
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchAllProjects(); // Fetch all projects for analytics
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchProjects(selectedCustomer.id);
    }
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
      if (response.data.length > 0 && !selectedCustomer) {
        setSelectedCustomer(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (customerId) => {
    try {
      const response = await axios.get(`${API}/projects?customer_id=${customerId}`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setAllProjects(response.data);
    } catch (error) {
      console.error('Error fetching all projects:', error);
    }
  };

  const downloadDocumentation = () => {
    // Trigger browser print dialog for PDF
    window.open('/AURA_Executive_Documentation_Package.html', '_blank');
  };

  // Calculate analytics
  const calculateAnalytics = () => {
    const phaseDistribution = {
      discovery: 0,
      analysis: 0,
      planning: 0,
      execution: 0,
      completed: 0
    };
    
    allProjects.forEach(p => {
      if (phaseDistribution[p.status] !== undefined) {
        phaseDistribution[p.status]++;
      }
    });
    
    return {
      totalCustomers: customers.length,
      totalProjects: allProjects.length,
      activeProjects: allProjects.filter(p => p.status !== 'completed').length,
      completedProjects: allProjects.filter(p => p.status === 'completed').length,
      phaseDistribution
    };
  };
  
  const analytics = (customers.length > 0 || allProjects.length > 0) ? calculateAnalytics() : null;

  const createCustomer = async (customerData) => {
    try {
      await axios.post(`${API}/customers`, customerData);
      await fetchCustomers();
      await fetchAllProjects();
      setShowCreateCustomer(false);
    } catch (error) {
      console.error('Error creating customer:', error);
      alert('Failed to create customer');
    }
  };

  const createProject = async (projectData) => {
    try {
      const response = await axios.post(`${API}/projects`, {
        ...projectData,
        customer_id: selectedCustomer.id
      });
      await axios.post(`${API}/mock-data/generate/${response.data.id}`, {});
      await fetchProjects(selectedCustomer.id);
      await fetchAllProjects();
      setShowCreateModal(false);
      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      await fetchProjects(selectedCustomer.id);
      await fetchAllProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer and ALL its projects? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      await fetchCustomers();
      await fetchAllProjects();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Top Navigation Bar */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-4 flex justify-between items-center">
          <div 
            onClick={onBackToHome}
            className="flex items-center space-x-3 cursor-pointer group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-all">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AURA
              </h1>
              <p className="text-xs text-gray-500 font-medium">AI Upgrade & Readiness Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {onViewDocumentation && (
              <button
                onClick={onViewDocumentation}
                className="text-blue-600 hover:text-blue-700 flex items-center space-x-2 font-medium transition-colors group px-4 py-2 rounded-lg hover:bg-blue-50 border-2 border-blue-200"
              >
                <BookOpen className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>📚 Documentation</span>
              </button>
            )}
            {user && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-lg">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                  ) : (
                    <User className="w-5 h-5 text-gray-600" />
                  )}
                  <span className="text-sm font-medium text-gray-700">{user.name}</span>
                </div>
                <button
                  onClick={logout}
                  className="text-red-600 hover:text-red-700 flex items-center space-x-2 font-medium transition-colors group px-4 py-2 rounded-lg hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">My Customers & Projects</h1>
              <p className="text-lg text-gray-600">Manage your customers and their SAP migration projects</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCreateCustomer(true)}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-xl transition-all duration-300 font-medium flex items-center space-x-2 transform hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                <span>New Customer</span>
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="w-10 h-10 opacity-80" />
                <span className="text-3xl font-bold">{analytics.totalCustomers}</span>
              </div>
              <p className="text-blue-100 text-sm font-medium">Total Customers</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <Folder className="w-10 h-10 opacity-80" />
                <span className="text-3xl font-bold">{analytics.totalProjects}</span>
              </div>
              <p className="text-purple-100 text-sm font-medium">Total Projects</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-10 h-10 opacity-80" />
                <span className="text-3xl font-bold">{analytics.activeProjects}</span>
              </div>
              <p className="text-green-100 text-sm font-medium">Active Projects</p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle2 className="w-10 h-10 opacity-80" />
                <span className="text-3xl font-bold">{analytics.completedProjects}</span>
              </div>
              <p className="text-orange-100 text-sm font-medium">Completed</p>
            </div>
            
            <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-10 h-10 opacity-80" />
                <span className="text-3xl font-bold">
                  {analytics.totalProjects > 0 
                    ? Math.round((analytics.completedProjects / analytics.totalProjects) * 100)
                    : 0}%
                </span>
              </div>
              <p className="text-pink-100 text-sm font-medium">Success Rate</p>
            </div>
          </div>
        )}

        {/* Phase Distribution Chart */}
        {analytics && analytics.totalProjects > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Projects by Phase</h3>
            <div className="grid grid-cols-5 gap-4">
              {[
                { phase: 'discovery', label: 'Discovery', color: 'bg-blue-500', count: analytics.phaseDistribution.discovery },
                { phase: 'analysis', label: 'Analysis', color: 'bg-purple-500', count: analytics.phaseDistribution.analysis },
                { phase: 'planning', label: 'Planning', color: 'bg-yellow-500', count: analytics.phaseDistribution.planning },
                { phase: 'execution', label: 'Execution', color: 'bg-orange-500', count: analytics.phaseDistribution.execution },
                { phase: 'completed', label: 'Completed', color: 'bg-green-500', count: analytics.phaseDistribution.completed },
              ].map((item) => (
                <div key={item.phase} className="text-center">
                  <div className={`${item.color} rounded-lg p-8 mb-3 shadow-md relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-white/10"></div>
                    <span className="text-4xl font-bold text-white relative z-10">{item.count}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{item.label}</p>
                  <p className="text-xs text-gray-500">
                    {analytics.totalProjects > 0 
                      ? Math.round((item.count / analytics.totalProjects) * 100)
                      : 0}%
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {customers.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Building2 className="w-20 h-20 mx-auto mb-6 text-gray-300" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Customers Yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">Create your first customer to start managing SAP migration projects</p>
            <button
              onClick={() => setShowCreateCustomer(true)}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create First Customer</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Customers List */}
            <div className="lg:col-span-1 space-y-3">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Customers ({customers.length})</h3>
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`bg-white rounded-xl p-4 cursor-pointer border-2 transition-all group ${
                    selectedCustomer?.id === customer.id
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">{customer.name}</h4>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">{customer.description}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCustomer(customer.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                  {customer.industry && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full capitalize">
                      {customer.industry}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Projects List */}
            <div className="lg:col-span-3">
              {selectedCustomer ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Projects for {selectedCustomer.name}
                    </h3>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>New Project</span>
                    </button>
                  </div>

                  {projects.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                      <Folder className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h4 className="text-xl font-semibold text-gray-900 mb-3">No Projects Yet</h4>
                      <p className="text-gray-600 mb-6">Create your first project for {selectedCustomer.name}</p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Create Project</span>
                      </button>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {projects.map((project) => (
                        <div
                          key={project.id}
                          className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-xl transition-all group"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div 
                              className="flex-1 cursor-pointer"
                              onClick={() => onSelectProject(project)}
                            >
                              <h4 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                                {project.name}
                              </h4>
                              <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProject(project.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-100 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-500 mb-3">
                            <span className="px-2 py-1 bg-gray-100 rounded capitalize">{project.migration_type}</span>
                            <span>{project.source_system} → {project.target_system}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                              project.status === 'completed' ? 'bg-green-100 text-green-700' :
                              project.status === 'execution' ? 'bg-blue-100 text-blue-700' :
                              project.status === 'planning' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {project.status}
                            </span>
                            <button
                              onClick={() => onSelectProject(project)}
                              className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
                            >
                              <span>Open</span>
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600">Select a customer to view their projects</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateCustomer && (
        <CreateCustomerModal
          onClose={() => setShowCreateCustomer(false)}
          onCreate={createCustomer}
        />
      )}

      {showCreateModal && selectedCustomer && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createProject}
          customer={selectedCustomer}
        />
      )}
    </div>
  );
};

const CreateProjectModal = ({ onClose, onCreate, customer }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    migration_type: 'brownfield',
    source_system: 'ECC 6.0 EHP8',
    target_system: 'S/4HANA 2023',
    owner_id: 'user-001'
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    await onCreate(formData);
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Create Migration Project</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Project Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="e.g., Global ECC to S/4HANA Migration"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              rows="4"
              placeholder="Brief description of the migration project"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Migration Type *</label>
            <select
              value={formData.migration_type}
              onChange={(e) => setFormData({ ...formData, migration_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="brownfield">Brownfield (System Conversion)</option>
              <option value="bluefield">Bluefield (Selective Data Transition)</option>
              <option value="greenfield">Greenfield (New Implementation)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Source System *</label>
              <select
                value={formData.source_system}
                onChange={(e) => setFormData({ ...formData, source_system: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option>ECC 6.0 EHP8</option>
                <option>ECC 6.0 EHP7</option>
                <option>ECC 6.0 EHP6</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Target System *</label>
              <select
                value={formData.target_system}
                onChange={(e) => setFormData({ ...formData, target_system: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option>S/4HANA 2024</option>
                <option>S/4HANA 2023</option>
                <option>S/4HANA 2022</option>
                <option>S/4HANA 2021</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {creating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateCustomerModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    contact_email: '',
    contact_phone: ''
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    await onCreate(formData);
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Create New Customer</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="e.g., Acme Corporation"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              rows="3"
              placeholder="Brief description of the customer"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
            <select
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">Select Industry</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="retail">Retail</option>
              <option value="finance">Finance</option>
              <option value="healthcare">Healthcare</option>
              <option value="technology">Technology</option>
              <option value="energy">Energy</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="contact@customer.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone</label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {creating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Create Customer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

